/* Tenant directory for the unscoped customer PWA landing page.
 * Uses the global `/api/v1/public/customer-tenants` endpoint (no tenant prefix).
 * Response is already filtered server-side (G2 — same DISABLED exclusion as Obchody).
 */

import {
  normalizeConsumerPublicTenantRow,
  type ConsumerPublicTenantRow,
} from 'pi-kiosk-shared';
import {
  customerEnvelopeApiError,
  customerInvalidJsonApiError,
} from '../../shared/errors/customerApiParseErrors.js';
import { stormAwareFetch } from '../../shared/http/stormAwareFetch.js';
import { rememberPublicCustomerListableTenantCodes } from '../shop/customerDiscoveryListability.js';

/** Live Organizace thumbs use square `logoUrl` only; wire `wordmarkUrl` is ignored. */
export type PublicCustomerTenantDTO = ConsumerPublicTenantRow;

interface PublicCustomerTenantsEnvelope {
  success: true;
  data: { tenants: PublicCustomerTenantDTO[] };
}

interface PublicCustomerTenantsErrorEnvelope {
  success: false;
  error: string;
  code?: string;
}

export async function fetchPublicCustomerTenants(
  apiBaseUrl: string,
  signal?: AbortSignal
): Promise<PublicCustomerTenantDTO[]> {
  const base = apiBaseUrl.replace(/\/+$/, '');
  const response = await stormAwareFetch(`${base}/api/v1/public/customer-tenants`, {
    method: 'GET',
    headers: { Accept: 'application/json' },
    // Avoid stale logoUrl (galleryVersion) from HTTP cache after branding upload.
    cache: 'no-store',
    signal,
  });

  let body: PublicCustomerTenantsEnvelope | PublicCustomerTenantsErrorEnvelope;
  try {
    body = (await response.json()) as
      | PublicCustomerTenantsEnvelope
      | PublicCustomerTenantsErrorEnvelope;
  } catch {
    throw customerInvalidJsonApiError(response.status);
  }

  if (!response.ok || body.success !== true) {
    throw customerEnvelopeApiError(response.status, body);
  }

  const tenants: PublicCustomerTenantDTO[] = [];
  for (const raw of body.data.tenants) {
    const row = normalizeConsumerPublicTenantRow(raw);
    if (row !== null) {
      tenants.push(row);
    }
  }
  // G2 — seed sync listability so resolveShopHref won't land on unlistable JWT home.
  rememberPublicCustomerListableTenantCodes(tenants.map((tenant) => tenant.code));
  return tenants;
}
